import React, { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  getERatingAll,
  deleteERating,
  createERating,
  updateERating,
  softDeleteERating,
} from "../../Services/ERatingService";
import Swal from "sweetalert2";
import MTable from "../../Components/MTable/MTable";
import { InputSwitch } from "primereact/inputswitch";
import Overlay from "../../Components/Overlay/Overlay";
import { toastError, toastSuccess } from "../../Components/Toasts/Toast";
import { handlerFormError, uuid } from "../../Utils/Utils";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import EditButton from "../../Components/Button/EditButton";
import DeleteButton from "../../Components/Button/DeleteButton";

let processingId = -1;

const ElectricalRatingPage = () => {
  let { userInfo } = useSelector((state) => state.auth);
  const [showAll, setShowAll] = useState(false);
  const mTable = useRef();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      code: "",
      name: "",
      is_active: true,
    },
  });
  const [state, setState] = useState({
    showForm: false,
    rating: {
      code: "",
      name: "",
      is_active: true,
    },
    processing: false,
  });

  const startProcessing = () => {
    processingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearTimeout(processingId);
    setState({ ...state, processing: false });
  };

  const removeData = (id) => {
    deleteERating(
      id,
      (res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Delete data success",
            text: "Data has been deleted!",
          }).then((res) => {
            mTable.current.refresh();
          });
        }
      },
      ({ response: { data } }) => {
        Swal.fire({
          icon: "error",
          title: "Delete data fail",
          text: data.message,
        });
      }
    );
  };

  const onEdit = (item) => () => {
    if (!userInfo.access.master_electrical_rating.can_update) {
      return;
    }
    reset(item);
    setState({ ...state, rating: item, showForm: true });
  };

  const onRemove = (item) => () => {
    if (!userInfo.access.master.can_delete) {
      toastError("You dont have permission for this action");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure?",
      text: "Deleted data can not be restored!",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        removeData(item.id);
      }
    });
  };

  const onSubmit = (data) => {
    startProcessing();
    const response = rating.id
      ? updateERating(rating.id, data)
      : createERating(data);
    response
      .then((res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Save data success",
            text: "Data has been saved!",
          }).then((r) => {
            mTable.current.refresh();
            rating.id ? onCloseForm() : onReset();
          });
        }
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const onReset = () => {
    reset({
      code: "",
      name: "",
      is_active: true,
    });
    setState({
      ...state,
      rating: {},
    });
  };

  const onCloseForm = () => {
    reset({
      code: "",
      name: "",
      is_active: true,
    });
    setState({ ...state, rating: {}, showForm: false });
  };

  const { rating, processing, showForm } = state;

  const onActiveChange = (item) => (e) => {
    if (!userInfo.access.master.can_update) {
      toastError("You dont have permission for this action");
      return;
    }

    const status = e.target.value ? "Activate" : "Inactive";
    softDeleteERating(item.id, { is_active: e.target.value })
      .then((res) => {
        toastSuccess(`${status} ${item.name} success`);
        mTable.current.refresh();
      })
      .catch(({ response: { err } }) => {
        toastError(`${status} ${item.name} Failed.`);
      });
  };

  const onAddData = () => {
    if (!userInfo.access.master_electrical_rating.can_create) {
      return;
    }
    onReset();
    setState({ ...state, showForm: true });
  };

  const columns = [
    {
      id: uuid(),
      title: "Electrical Rating Name",
      field: "name",
      sortable: true,
    },
    {
      id: uuid(),
      title: "Electrical Rating Code",
      field: "code",
      sortable: true,
    },
    {
      id: uuid(),
      title: "Active",
      field: "is_active",
      render: (item) => {
        if (item.is_active === true) {
          return <span className="badge badge-success">Active</span>;
        } else {
          return <span className="badge badge-secondary">Inactive</span>;
        }
      },
    },
    {
      id: uuid(),
      title: "",
      sortable: true,
      style: { width: 200 },
      render: (item) => {
        return (
          <div>
            {userInfo.access.master_electrical_rating.can_update && (
              <EditButton title="Edit" onClick={onEdit(item)} />
            )}
            {userInfo.access.master_electrical_rating.can_delete && (
              <DeleteButton title="Remove" onClick={onRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  const showAllFunc = () => {
    setShowAll(!showAll);
    mTable.current.refresh();
  };

  const propsTable = {
    columns,
    showAllState: showAll,
    showAllFunc,
    getData: (params) => {
      if (showAll === false) {
        if (params.filterAnd) params.filterAnd += "," + `is_active:true`;
        else params.filterAnd = "is_active:true";
      }
      return getERatingAll({ ...params });
    },
    showIndex: true,
    enableFilter: true,
    showAddButton: !userInfo.access.master_electrical_rating.can_create
      ? false
      : true,
  };

  return (
    <div>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h4
                  style={{
                    marginTop: "0.5rem",
                    marginLeft: "0.2rem",
                    fontWeight: "400",
                  }}
                >
                  {showForm
                    ? "Electrical Rating Form"
                    : "Electrical Rating Management"}
                </h4>
                <h6
                  style={{
                    marginLeft: "0.2rem",
                    fontWeight: "200",
                    letterSpacing: "1.2px",
                  }}
                >
                  {showForm
                    ? "Add/Edit a Electrical Rating"
                    : "Database of Electrical Ratings"}
                </h6>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <NavLink to="/home">Home</NavLink>
                  </li>
                  <li className="breadcrumb-item active">Electrical Rating</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <Overlay display={processing} />

                  <form name="form-detail" onSubmit={handleSubmit(onSubmit)}>
                    <div className="card-body">
                      <div
                        className={`${
                          !showForm
                            ? "card__side--front-show"
                            : "card__side--front"
                        }`}
                      >
                        <MTable
                          ref={mTable}
                          {...propsTable}
                          onAddData={onAddData}
                          showIndex={true}
                        />
                      </div>

                      <div
                        className={`${
                          showForm
                            ? "card__side--back-show"
                            : "card__side--back"
                        }`}
                      >
                        <div className="row">
                          <div className="col-md-4">
                            <div className="form-group">
                              <label htmlFor="code">
                                Electrical Rating Code
                              </label>
                              <input
                                id="code"
                                {...register("code", {
                                  required: {
                                    value: true,
                                    message:
                                      "Electrical Rating code is required!",
                                  },
                                })}
                                placeholder="Electrical Rating code"
                                className="form-control"
                                disabled={rating.id ? true : false}
                              />
                              {errors.code && (
                                <span
                                  className="text-danger"
                                  style={{ fontSize: 14 }}
                                >
                                  {errors.code.message}
                                </span>
                              )}
                            </div>
                            <div className="form-group">
                              <label htmlFor="name">
                                Electrical Rating Name
                              </label>
                              <input
                                id="name"
                                {...register("name", {
                                  required: {
                                    value: true,
                                    message:
                                      "Electrical Rating name is required!",
                                  },
                                })}
                                placeholder="Electrical Rating name"
                                className="form-control"
                              />
                              {errors.name && (
                                <span
                                  className="text-danger"
                                  style={{ fontSize: 14 }}
                                >
                                  {errors.name.message}
                                </span>
                              )}
                            </div>
                            <div className="form-group">
                              <label htmlFor="is_active">Active</label>
                              <div>
                                <Controller
                                  name="is_active"
                                  control={control}
                                  render={({ field }) => {
                                    return (
                                      <InputSwitch
                                        {...field}
                                        checked={field.value}
                                      />
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {showForm && (
                      <div className="card-footer delay">
                        <div className="row">
                          <div className="col-md-12">
                            <div
                              className="d-flex"
                              style={{ justifyContent: "flex-end" }}
                            >
                              <button
                                type="button"
                                className="btn btn-default mr-3"
                                style={{ width: 100 }}
                                onClick={onCloseForm}
                              >
                                <i className="fa fa-times" /> Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger mr-3"
                                style={{ width: 100 }}
                                onClick={onReset}
                              >
                                <i className="fa fa-sync" /> Clear
                              </button>
                              <button
                                type="submit"
                                className="btn btn-dark"
                                style={{ width: 100 }}
                                disabled={
                                  control._formValues.id
                                    ? !userInfo.access.master.can_update
                                    : !userInfo.access.master.can_create
                                }
                              >
                                <i className="fa fa-save" /> Save
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ElectricalRatingPage;
